import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import type {ZodType} from "zod";
import {buildPlutoCacheableGetUrl} from "~/utilities/plutoCacheableGetUrl";

export type PlutoWorkspaceClientContext = {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
};

function normalizePlutoPath(path: string): string {
    if (path.startsWith("/")) {
        return path;
    }
    return `/${path}`;
}

export async function plutoPost<T>({
    accessToken,
    body,
    path,
    intellsysBaseUrl,
    responseValidator,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    body: unknown;
    path: string;
    responseValidator: ZodType<T>;
}): Promise<Result<T>> {
    return robustJsonResultClientFetch<T>(
        `${intellsysBaseUrl}/sub-system/pluto${normalizePlutoPath(path)}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify(body),
        },
        responseValidator,
    );
}

export async function plutoCacheableGet<T>({
    accessToken,
    input,
    intellsysBaseUrl,
    path,
    responseValidator,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    input: unknown;
    path: string;
    responseValidator: ZodType<T>;
}): Promise<Result<T>> {
    return robustJsonResultClientFetch<T>(
        buildPlutoCacheableGetUrl({
            input: input,
            intellsysBaseUrl: intellsysBaseUrl,
            path: path,
        }),
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-Workspace-Id": workspaceId,
            },
        },
        responseValidator,
    );
}

export type PlutoPdfBlobResult = {
    blob: Blob;
    contentDisposition: string | null;
};

export async function plutoFetchPdfBlob({
    accessToken,
    body,
    path,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    body: unknown;
    path: string;
}): Promise<Result<PlutoPdfBlobResult>> {
    try {
        const response = await fetch(`${intellsysBaseUrl}/sub-system/pluto${normalizePlutoPath(path)}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": workspaceId,
            },
            body: JSON.stringify(body),
        });
        if (response.ok !== true) {
            return {
                success: false,
                error: new Error(`PDF fetch failed: ${response.status} ${response.statusText}`),
            };
        }
        const blob = await response.blob();
        return dataResult({
            blob: blob,
            contentDisposition: response.headers.get("Content-Disposition"),
        });
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error : new Error("PDF fetch failed"),
        };
    }
}
